import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';

// --- Localization IDs and Wrappers ---

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.quotes.QuoteTechnicalReviewConfirmation.';

  public static readonly technicalReviewTitle = `${Id.prefix}technicalReviewTitle`;
  public static readonly franchisee = `${Id.prefix}franchisee`;
  public static readonly orderNumberRequired = `${Id.prefix}orderNumberRequired`;
  public static readonly cancelButton = `${Id.prefix}cancelButton`;
  public static readonly sendButton = `${Id.prefix}sendButton`;
  public static readonly noThanksButton = `${Id.prefix}noThanksButton`;
  public static readonly yesButton = `${Id.prefix}yesButton`;
  public static readonly technicalReviewDirectToSupplier = `${Id.prefix}technicalReviewDirectToSupplier`;
  public static readonly technicalReviewWithApproval = `${Id.prefix}technicalReviewWithApproval`;
  public static readonly dealerOrderNoLabel = `${Id.prefix}dealerOrderNoLabel`;
  public static readonly purchaseOrderTitle = `${Id.prefix}purchaseOrderTitle`;
  public static readonly okButton = `${Id.prefix}okButton`;
  public static readonly approvalIssuesToSupplier = `${Id.prefix}approvalIssuesToSupplier`;
  public static readonly approvalOrderNumber = `${Id.prefix}approvalOrderNumber`;
}

export class LangQuoteTechnicalReviewConfirmation {
  private constructor() {}

  public static technicalReviewTitle(): string {
    return interpolate(Id.technicalReviewTitle);
  }

  public static franchisee(): string {
    return interpolate(Id.franchisee);
  }

  public static orderNumberRequired(params: { dealerName: string }): string {
    return interpolate(Id.orderNumberRequired, params);
  }

  public static cancelButton(): string {
    return interpolate(Id.cancelButton);
  }

  public static sendButton(): string {
    return interpolate(Id.sendButton);
  }

  public static noThanksButton(): string {
    return interpolate(Id.noThanksButton);
  }

  public static yesButton(): string {
    return interpolate(Id.yesButton);
  }

  public static technicalReviewDirectToSupplier(params: { supplierName: string }): string {
    return interpolate(Id.technicalReviewDirectToSupplier, params);
  }

  public static technicalReviewWithApproval(params: { supplierName: string }): string {
    return interpolate(Id.technicalReviewWithApproval, params);
  }

  public static dealerOrderNoLabel(params: { dealerName: string }): string {
    return interpolate(Id.dealerOrderNoLabel, params);
  }

  public static purchaseOrderTitle(): string {
    return interpolate(Id.purchaseOrderTitle);
  }

  public static okButton(): string {
    return interpolate(Id.okButton);
  }

  public static approvalIssuesToSupplier(params: { supplierName: string }): string {
    return interpolate(Id.approvalIssuesToSupplier, params);
  }

  public static approvalOrderNumber(params: { supplierName: string }): string {
    return interpolate(Id.approvalOrderNumber, params);
  }
}

// --- Registration Module ---

export class QuoteTechnicalReviewConfirmationModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.technicalReviewTitle, 'Technical Review', 'Dialog title for technical review confirmation');
    register.add(Id.franchisee, 'Franchisee', 'Fallback name for the dealer/franchisee');
    register.add(
      Id.orderNumberRequired,
      '{{dealerName}} Order No is required',
      'Message shown when order number is required and missing'
    );
    register.add(Id.cancelButton, 'Cancel', 'Button label for cancel');
    register.add(Id.sendButton, 'Send', 'Button label for send');
    register.add(Id.noThanksButton, 'No thanks', 'Button label for no thanks');
    register.add(Id.yesButton, 'Yes', 'Button label for yes');
    register.add(
      Id.technicalReviewDirectToSupplier,
      `This {{glossary 'quote'}} will now be submitted to **{{supplierName}}** for a 
{{glossary 'technical-review'}}.

*If approved without changes, the {{glossary 'purchase-order-abbrev'}} will be dispatched to* **{{supplierName}}**`,
      'Message for direct to supplier technical review'
    );
    register.add(
      Id.technicalReviewWithApproval,
      `This {{glossary 'quote'}} will now be submitted to **{{supplierName}}** for a 
{{glossary 'technical-review'}}.

If approved without changes, would you like **{{supplierName}}** to {{glossary 'purchase-order-state-issued'}} the {{glossary 'purchase-order-abbrev'}} on your behalf? If not, a {{glossary 'purchase-order-state-draft'}} 
{{glossary 'purchase-order-abbrev'}} will be generated for you to {{glossary 'purchase-order-state-issued'}}.`,
      'Message for technical review with approval'
    );
    register.add(Id.dealerOrderNoLabel, '{{dealerName}} Order No:', 'Label for dealer order number input');
    register.add(
      Id.purchaseOrderTitle,
      `{{glossary 'purchase-order'}}`,
      'Dialog title when approving a quote without a technical review'
    );
    register.add(Id.okButton, 'OK', 'Button label for ok');
    register.add(
      Id.approvalIssuesToSupplier,
      `Approving this {{glossary 'quote'}} will send the {{glossary 'purchase-order-abbrev'}} to **{{supplierName}}** immediately.`,
      'Message when approving without a technical review and the supplier issues orders immediately'
    );
    register.add(
      Id.approvalOrderNumber,
      `Approving this {{glossary 'quote'}} will create a {{glossary 'purchase-order-abbrev'}} for **{{supplierName}}**.`,
      'Message when approving without a technical review and the supplier requires an order number'
    );
  }
}
